import { CreationYearOptions } from "@faire/deprecated-retailer-visitor-shared/consts/creationYearOptions";
import {
  SignUpPill,
  SignUpPillContainer,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/SignUpPill";
import { useSignUpEventTracker } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Flex } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Form, useFormikContext } from "formik";
import isUndefined from "lodash/isUndefined";
import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";

export type OpeningYearData = Pick<ISignUpRequest, "reported_creation_time">;

export const OpeningYearForm: FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { trackFieldValue } = useSignUpEventTracker(Step.OPENING_YEAR);
  const creationYears = CreationYearOptions(strictLocalize, true);

  const { values, handleSubmit, setFieldValue, isValid } =
    useFormikContext<OpeningYearData>();
  const [shouldAutoProgress] = useState(
    isUndefined(values.reported_creation_time)
  );

  const delayedHandleSubmit = useDelayedCallback(handleSubmit);

  useEffect(() => {
    if (shouldAutoProgress && values.reported_creation_time) {
      delayedHandleSubmit(800);
    }
  }, [delayedHandleSubmit, shouldAutoProgress, values.reported_creation_time]);

  return (
    <Form
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <YearsContainer>
        {creationYears.map((year) => (
          <SignUpPill
            key={year.label}
            title={year.label}
            isSelected={year.value === values.reported_creation_time}
            onClick={() => {
              trackFieldValue("reported_creation_time", year.value ?? "");
              setFieldValue("reported_creation_time", year.value);
            }}
            dataTestId={`PILL_${year.value}`}
          />
        ))}
      </YearsContainer>
      {!shouldAutoProgress ? (
        <>
          <Spacer height="4x" />
          <Button data-test-id="next" disabled={!isValid} type="submit">
            <StrictLocalMsg
              id="m853T8"
              defaultMessage="Next"
              description={{ text: "Button to visit the next page" }}
            />
          </Button>
        </>
      ) : null}
    </Form>
  );
};

export const useSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  return useMemo(
    () =>
      Yup.object().shape({
        reported_creation_time: Yup.string().required(
          strictLocalize({
            id: "PPLP3R",
            defaultMessage: "Please select an option",
          })
        ),
      }),
    [strictLocalize]
  );
};

const YearsContainer = styled(Flex)`
  &&& {
    display: grid;
  }
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${getSpacing(12)};

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-wrap: wrap;
    justify-content: space-between;
    gap: ${getSpacing("2x")};
  }
  ${SignUpPillContainer} {
    width: 100%;
    ${responsiveMediaQueries.tabletAndAbove} {
      flex: 0 50%;
    }
  }
`;
